import React, { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import api from 'services/api'

const SlackCallback = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const hasRun = useRef(false)

  const [status, setStatus] = useState('loading')
  const [error, setError] = useState(null)

  useEffect(() => {
    // Authorization codes are single-use; guard against StrictMode's
    // double-invoked effect re-POSTing the same (now-consumed) code.
    if (hasRun.current) return
    hasRun.current = true

    const code = searchParams.get('code')
    const state = searchParams.get('state')
    const oauthError = searchParams.get('error')

    if (oauthError) {
      setStatus('error')
      setError('Slack authorization was cancelled or denied.')
      return
    }
    if (!code || !state) {
      setStatus('error')
      setError('Missing authorization details from Slack.')
      return
    }

    const complete = async () => {
      try {
        await api.slack.completeCallback(code, state)
        setStatus('success')
        setTimeout(() => navigate('/chat', { replace: true }), 1200)
      } catch (err) {
        setStatus('error')
        setError(err.message)
      }
    }
    complete()
  }, [searchParams, navigate])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        maxWidth: 480,
        margin: '160px auto',
        textAlign: 'center',
      }}
    >
      {status === 'loading' && (
        <>
          <CircularProgress />
          <Typography>Connecting your Slack account…</Typography>
        </>
      )}
      {status === 'success' && (
        <Typography variant="h6" color="primary">
          Slack connected. Redirecting…
        </Typography>
      )}
      {status === 'error' && (
        <>
          <Typography variant="h6" color="error">
            Couldn't connect Slack
          </Typography>
          <Typography color="text.secondary">{error}</Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/chat', { replace: true })}
          >
            Back to Chat
          </Button>
        </>
      )}
    </Box>
  )
}

export default SlackCallback
